'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { t, tFromTo, tAudience } from '@/data/translations';

interface WriteOwnSheetProps {
  isOpen: boolean;
  prefillText: string;
  onClose: () => void;
  onSubmit: (text: string) => void;
  countryName: string;
  countryFlag: string;
  direction: 'to_america' | 'to_world';
  langCode: string;
}

export default function WriteOwnSheet({
  isOpen,
  prefillText,
  onClose,
  onSubmit,
  countryName,
  countryFlag,
  direction,
  langCode,
}: WriteOwnSheetProps) {
  const audienceLabel = direction === 'to_world' ? t('world', langCode) : t('america', langCode);
  const headerLine = tFromTo(langCode, countryName, audienceLabel);
  const placeholder = tAudience('writePlaceholder', audienceLabel, langCode);
  const [text, setText] = useState(prefillText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setText(prefillText);
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [isOpen, prefillText]);

  const handleSubmit = () => {
    if (text.trim().length > 0) {
      onSubmit(text.trim());
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            className="fixed inset-x-0 bottom-0 z-50 bg-slate-900 rounded-t-3xl border-t border-slate-700/50 px-5 pt-4 pb-8 max-h-[85vh] flex flex-col"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            {/* Handle */}
            <div className="w-10 h-1 bg-slate-700 rounded-full mx-auto mb-4" />

            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xl shrink-0">{countryFlag}</span>
                <span className="text-slate-300 text-sm font-medium" lang={langCode}>
                  {headerLine}
                </span>
              </div>
              <button
                onClick={onClose}
                className="text-slate-500 hover:text-white transition-colors p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="flex-1 min-h-[200px] bg-slate-800 text-white text-lg rounded-2xl p-4 border border-slate-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none resize-none placeholder-slate-600"
              placeholder={placeholder}
              maxLength={500}
              lang={langCode}
            />

            {/* Character count */}
            <div className="text-right text-xs text-slate-600 mt-1 mr-1">
              {text.length}/500
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-3">
              <button
                onClick={() => setText('')}
                className="flex-1 py-3 rounded-2xl bg-slate-800 text-slate-400 font-medium border border-slate-700 hover:bg-slate-750 hover:text-white transition-colors"
                lang={langCode}
              >
                {t('startOver', langCode)}
              </button>
              <button
                onClick={handleSubmit}
                disabled={text.trim().length === 0}
                className="flex-2 py-3 px-8 rounded-2xl bg-sky-500 text-white font-bold shadow-lg shadow-sky-500/25 disabled:opacity-40 disabled:shadow-none hover:bg-sky-400 transition-colors"
                lang={langCode}
              >
                {t('submit', langCode)}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
